import * as THREE from "three";
import { planDeckSupports } from "../domain/deck/calculateDeck";
import {
	getDeckFootprintRects,
	getDeckOpenEdges,
} from "../domain/deck/deckFootprint";
import { setConcreteContactSpan } from "./concreteVariation";
import { addBox } from "./geometry";
import {
	matConcrete,
	matDeckBoard,
	matMetal,
	matTreatedWood,
	matWood,
} from "./materials";
import type {
	DeckCalculationResult,
	DeckConfig,
	SaunaDimensions,
} from "./types";

const BOARD_THICKNESS_M = 0.028;
const JOIST_HEIGHT_M = 0.145;
const JOIST_WIDTH_M = 0.045;
const BEARER_HEIGHT_M = 0.17;
const BEARER_WIDTH_M = 0.045;
const NOGGING_LENGTH_M = 0.4;
/**
 * Ground to bearer underside. The bearer used to hang 20 mm below the joists
 * with nothing between them; closing that gap lifted the frame, so the plint
 * grew by the same 20 mm to keep its foot on the ground.
 */
const PIER_VISIBLE_HEIGHT_M = 0.37;
/** Plintbeslag: same proportions as the sauna's own stolpsko. */
const SHOE_HEIGHT_M = 0.1;
const SHOE_LENGTH_M = 0.05;
const PIER_RADIUS_M = 0.08;

function createDeckBoardMaterial(index: number): THREE.Material {
	const material = matDeckBoard.clone();
	if (material instanceof THREE.MeshStandardMaterial) {
		const hueOffset = ((index % 5) - 2) * 0.012;
		const lightnessOffset = ((index % 7) - 3) * 0.018;
		material.color.offsetHSL(hueOffset, 0, lightnessOffset);
		material.roughness = Math.max(
			0.72,
			Math.min(0.96, material.roughness + ((index % 4) - 1.5) * 0.025),
		);
	}
	return material;
}

/**
 * Walls whose exterior base trim (nederkantsavslut) must be suppressed because
 * an attached deck runs outward through the same just-proud-of-cladding slab.
 *
 * The base rect always butts the attach wall. Wrap/L arms run outboard of the
 * walls perpendicular to the attach wall, so their boards cross those walls'
 * base trim too:
 *  - `wrap-front` / `wrap-sides`: both perpendicular walls.
 *  - `l-shape`: the single perpendicular wall the arm extends toward (local +X
 *    after `transformDeckGroup`'s rotation).
 * Deriving from config (not per-rect geometry) keeps the full build and the
 * selective Exterior rebuild in exact agreement.
 */
export function getDeckSuppressedWalls(
	deckConfig: DeckConfig | null | undefined,
): DeckConfig["attachWall"][] {
	if (!deckConfig) return [];
	const { attachWall, shape } = deckConfig;
	const perpendicular: Record<
		DeckConfig["attachWall"],
		DeckConfig["attachWall"][]
	> = {
		front: ["left", "right"],
		back: ["left", "right"],
		left: ["front", "back"],
		right: ["front", "back"],
	};
	// Which perpendicular wall local +X maps to (see rotation math in
	// transformDeckGroup): front→right, back→left, left→front, right→back.
	const lShapeArmWall: Record<
		DeckConfig["attachWall"],
		DeckConfig["attachWall"]
	> = {
		front: "right",
		back: "left",
		left: "front",
		right: "back",
	};
	const suppressed = new Set<DeckConfig["attachWall"]>([attachWall]);
	if (shape === "wrap-front" || shape === "wrap-sides") {
		for (const wall of perpendicular[attachWall]) suppressed.add(wall);
	} else if (shape === "l-shape") {
		suppressed.add(lShapeArmWall[attachWall]);
	}
	return [...suppressed];
}

function transformDeckGroup(
	group: THREE.Group,
	attachWall: DeckConfig["attachWall"],
	saunaDimensions: SaunaDimensions,
) {
	switch (attachWall) {
		case "front":
			group.position.set(0, 0, saunaDimensions.length / 2);
			break;
		case "back":
			group.position.set(0, 0, -saunaDimensions.length / 2);
			group.rotation.y = Math.PI;
			break;
		case "left":
			group.position.set(-saunaDimensions.width / 2, 0, 0);
			group.rotation.y = -Math.PI / 2;
			break;
		case "right":
			group.position.set(saunaDimensions.width / 2, 0, 0);
			group.rotation.y = Math.PI / 2;
			break;
	}
}

export function buildDeckGeometry(
	config: DeckConfig,
	saunaDimensions: SaunaDimensions,
	saunaFoundationHeight: number,
	calculation: DeckCalculationResult,
): THREE.Group {
	const root = new THREE.Group();
	root.name = "detail/deck/root";

	const boardsGroup = new THREE.Group();
	boardsGroup.name = "detail/deck/boards";
	const joistsGroup = new THREE.Group();
	joistsGroup.name = "detail/deck/joists";
	const bearersGroup = new THREE.Group();
	bearersGroup.name = "detail/deck/bearers";
	const noggingsGroup = new THREE.Group();
	noggingsGroup.name = "detail/deck/noggings";
	const piersGroup = new THREE.Group();
	piersGroup.name = "detail/deck/piers";
	const fasciaGroup = new THREE.Group();
	fasciaGroup.name = "detail/deck/fascia";
	root.add(
		boardsGroup,
		joistsGroup,
		bearersGroup,
		noggingsGroup,
		piersGroup,
		fasciaGroup,
	);

	const boardTopY = saunaFoundationHeight;
	const boardCenterY = boardTopY - BOARD_THICKNESS_M / 2;
	const joistCenterY =
		boardCenterY - BOARD_THICKNESS_M / 2 - JOIST_HEIGHT_M / 2;
	const bearerCenterY = joistCenterY - JOIST_HEIGHT_M / 2 - BEARER_HEIGHT_M / 2;

	// The kantbräda sits inside the footprint, flush with the board ends, so
	// the frame (joists, bearers) is inset by its thickness on every side.
	const fasciaT = calculation.fascia.thicknessMm / 1000;
	const fasciaH = calculation.fascia.heightMm / 1000;

	const rects = getDeckFootprintRects(config, saunaDimensions);
	const boardPitchM = config.boardType === "22x95" ? 0.1 : 0.125;
	const joistCcM = config.boardType === "22x95" ? 0.45 : 0.6;
	let boardMaterialIndex = 0;
	let noggingIndex = 0;

	const supportPlans = planDeckSupports(rects);
	rects.forEach((rect, rectIndex) => {
		const boardCount = Math.max(1, Math.ceil(rect.depth / boardPitchM));
		const joistCount = Math.max(2, Math.ceil(rect.width / joistCcM) + 1);
		const { bearerZ } = supportPlans[rectIndex];
		const bearerCount = bearerZ.length;

		for (let index = 0; index < boardCount; index += 1) {
			const z =
				rect.centerZ -
				rect.depth / 2 +
				((index + 0.5) * rect.depth) / boardCount;
			const board = addBox(
				rect.width,
				BOARD_THICKNESS_M,
				Math.max(0.09, rect.depth / Math.max(boardCount, 1) - 0.005),
				createDeckBoardMaterial(boardMaterialIndex),
				rect.centerX,
				boardCenterY,
				z,
				boardsGroup,
			);
			boardMaterialIndex += 1;
			board.name = `detail/deck/board-${rectIndex + 1}-${index + 1}`;
		}

		// Joist centre lines, spread so the outer joists' faces meet the fascia.
		const frameInset = fasciaT + JOIST_WIDTH_M / 2;
		const joistX = (index: number) =>
			rect.centerX -
			rect.width / 2 +
			frameInset +
			(index * (rect.width - 2 * frameInset)) / Math.max(1, joistCount - 1);

		for (let index = 0; index < joistCount; index += 1) {
			const x = joistX(index);
			const joist = addBox(
				JOIST_WIDTH_M,
				JOIST_HEIGHT_M,
				rect.depth - 2 * fasciaT,
				matWood,
				x,
				joistCenterY,
				rect.centerZ,
				joistsGroup,
			);
			joist.name = `detail/deck/joist-${rectIndex + 1}-${index + 1}`;
		}

		for (let index = 0; index < bearerCount; index += 1) {
			const z = bearerZ[index];
			const bearer = addBox(
				rect.width - 2 * fasciaT,
				BEARER_HEIGHT_M,
				BEARER_WIDTH_M,
				matTreatedWood,
				rect.centerX,
				bearerCenterY,
				z,
				bearersGroup,
			);
			bearer.name = `detail/deck/bearer-${rectIndex + 1}-${index + 1}`;
		}

		for (
			let rowIndex = 0;
			rowIndex < Math.max(1, bearerCount - 1);
			rowIndex += 1
		) {
			const z =
				rect.centerZ -
				rect.depth / 2 +
				((rowIndex + 0.5) * rect.depth) / Math.max(1, bearerCount - 1);
			for (let spanIndex = 0; spanIndex < joistCount - 1; spanIndex += 1) {
				const leftJoistX = joistX(spanIndex);
				const rightJoistX = joistX(spanIndex + 1);
				const spanWidth = Math.max(
					0.1,
					rightJoistX - leftJoistX - JOIST_WIDTH_M,
				);
				const nogging = addBox(
					Math.min(NOGGING_LENGTH_M, spanWidth),
					JOIST_HEIGHT_M,
					JOIST_WIDTH_M,
					matWood,
					(leftJoistX + rightJoistX) / 2,
					joistCenterY,
					z,
					noggingsGroup,
				);
				noggingIndex += 1;
				nogging.name = `detail/deck/nogging-${rectIndex + 1}-${noggingIndex}`;
			}
		}
	});

	// Kantbräda along every open edge, top tight under the boards. Where two
	// runs meet at an outside corner the one running along z stops at the
	// other's inner face, so the corner is closed without overlapping faces.
	const fasciaCenterY = boardCenterY - BOARD_THICKNESS_M / 2 - fasciaH / 2;
	const edges = getDeckOpenEdges(config, saunaDimensions);
	edges.forEach((edge, index) => {
		let { from, to } = edge;
		if (edge.axis === "z") {
			for (const other of edges) {
				if (other.axis !== "x") continue;
				if (edge.at < other.from - 1e-6 || edge.at > other.to + 1e-6) continue;
				// The x-run covers [other.at - outward·T, other.at]; trim only
				// when this run lies on that inner side (an outside corner).
				if (Math.abs(other.at - from) < 1e-6 && other.outward === -1) {
					from += fasciaT;
				}
				if (Math.abs(other.at - to) < 1e-6 && other.outward === 1) {
					to -= fasciaT;
				}
			}
		}
		const length = to - from;
		const along = (from + to) / 2;
		const across = edge.at - (edge.outward * fasciaT) / 2;
		const fascia =
			edge.axis === "x"
				? addBox(
						length,
						fasciaH,
						fasciaT,
						createDeckBoardMaterial(boardMaterialIndex + index),
						along,
						fasciaCenterY,
						across,
						fasciaGroup,
					)
				: addBox(
						fasciaT,
						fasciaH,
						length,
						createDeckBoardMaterial(boardMaterialIndex + index),
						across,
						fasciaCenterY,
						along,
						fasciaGroup,
					);
		fascia.name = `detail/deck/fascia-${index + 1}`;
	});

	calculation.piers.layout.forEach((pier, index) => {
		const pierGeometry = new THREE.CylinderGeometry(
			PIER_RADIUS_M,
			PIER_RADIUS_M + 0.02,
			PIER_VISIBLE_HEIGHT_M,
			16,
		);
		// Lets matConcrete put its ground-contact band on the foot.
		setConcreteContactSpan(pierGeometry, PIER_VISIBLE_HEIGHT_M);
		const mesh = new THREE.Mesh(pierGeometry, matConcrete);
		mesh.position.set(
			pier.x,
			bearerCenterY - BEARER_HEIGHT_M / 2 - PIER_VISIBLE_HEIGHT_M / 2,
			pier.z,
		);
		mesh.castShadow = true;
		mesh.receiveShadow = true;
		mesh.name = `detail/deck/pier-${index + 1}`;
		piersGroup.add(mesh);

		// The bearer runs along x, so the shoe's plates flank it in z.
		const shoe = addBox(
			SHOE_LENGTH_M,
			SHOE_HEIGHT_M,
			BEARER_WIDTH_M + 0.01,
			matMetal,
			pier.x,
			bearerCenterY - BEARER_HEIGHT_M / 2 + SHOE_HEIGHT_M / 2,
			pier.z,
			piersGroup,
		);
		shoe.name = `detail/deck/shoe-${index + 1}`;
	});

	transformDeckGroup(root, config.attachWall, saunaDimensions);
	return root;
}
